import React from 'react';
import { ChevronRight } from 'lucide-react';
import {
    CommunityWhitelabelDashboardWidgetDto,
    CommunityWhitelabelDashboardWidgetLayout
} from '@api/ChallengeClient';
import { imageUrl, previewText, rowsOfWidgets } from './widget-content';

const Layout = CommunityWhitelabelDashboardWidgetLayout;

interface Branding {
    name: string;
    logoUrl?: string;
    accentColor: string;
}

interface WidgetContent {
    title?: string;
    text?: string;
    imageUrl?: string;
    buttonLabel?: string;
    hasLink: boolean;
}

interface CardProps {
    branding: Branding;
    content: WidgetContent;
}

const BrandHeader = ({
    branding,
    hasLink
}: {
    branding: Branding;
    hasLink: boolean;
}) => (
    <div className="flex items-center gap-2">
        {branding.logoUrl && (
            <img
                src={branding.logoUrl}
                alt=""
                className="size-6 rounded object-contain"
            />
        )}
        <span
            className="flex-1 truncate text-[11px] font-semibold tracking-widest uppercase"
            style={{ color: branding.accentColor }}
        >
            {branding.name}
        </span>
        {hasLink && (
            <ChevronRight
                className="size-3.5"
                style={{ color: branding.accentColor }}
            />
        )}
    </div>
);

const Title = ({ children }: { children?: string }) =>
    children ? (
        <p className="text-[15px] leading-5 font-semibold text-white">
            {children}
        </p>
    ) : null;

const Text = ({ children }: { children?: string }) =>
    children ? (
        <p className="text-[13px] leading-[18px] text-white/75">{children}</p>
    ) : null;

const WidgetImage = ({
    url,
    className
}: {
    url?: string;
    className: string;
}) =>
    url ? (
        <img src={url} alt="" className={`object-cover ${className}`} />
    ) : null;

const HeroCard = ({
    branding,
    content
}: {
    branding: Branding;
    content: WidgetContent;
}) => (
    <>
        <WidgetImage url={content.imageUrl} className="aspect-[2/1] w-full" />
        <div className="flex flex-col gap-2 p-3">
            <BrandHeader branding={branding} hasLink={content.hasLink} />
            <Title>{content.title}</Title>
            <Text>{content.text}</Text>
        </div>
    </>
);

const HeadlineCard = ({
    branding,
    content
}: {
    branding: Branding;
    content: WidgetContent;
}) => (
    <>
        <div className="flex flex-col gap-2 p-3">
            <BrandHeader branding={branding} hasLink={content.hasLink} />
            <Title>{content.title}</Title>
        </div>
        <WidgetImage url={content.imageUrl} className="aspect-[2/1] w-full" />
        {content.text && (
            <div className="p-3">
                <Text>{content.text}</Text>
            </div>
        )}
    </>
);

const CompactCard = ({
    branding,
    content
}: {
    branding: Branding;
    content: WidgetContent;
}) => (
    <div className="flex items-center gap-3 p-3">
        <WidgetImage
            url={content.imageUrl}
            className="size-[72px] shrink-0 rounded-lg"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
            {content.title && (
                <p className="truncate text-[15px] font-semibold text-white">
                    {content.title}
                </p>
            )}
            {content.text && (
                <p className="line-clamp-2 text-[13px] leading-[18px] text-white/75">
                    {content.text}
                </p>
            )}
        </div>
        {content.hasLink && (
            <ChevronRight
                className="size-4 shrink-0"
                style={{ color: branding.accentColor }}
            />
        )}
    </div>
);

const Logo = ({ branding }: { branding: Branding }) =>
    branding.logoUrl ? (
        <img
            src={branding.logoUrl}
            alt=""
            className="size-6 rounded object-contain"
        />
    ) : null;

const OverlayCard = ({
    branding,
    content,
    aspect
}: CardProps & { aspect: string }) => (
    <div className={`relative ${aspect}`}>
        <WidgetImage url={content.imageUrl} className="size-full" />
        <div className="absolute inset-0 flex flex-col justify-between bg-gradient-to-t from-black/85 via-black/20 to-transparent p-3">
            <div className="h-6">
                <Logo branding={branding} />
            </div>
            <div className="flex flex-col gap-1">
                <Title>{content.title}</Title>
                <Text>{content.text}</Text>
            </div>
        </div>
    </div>
);

const BannerCard = (props: CardProps) => (
    <OverlayCard {...props} aspect="aspect-video" />
);

const PosterCard = (props: CardProps) => (
    <OverlayCard {...props} aspect="aspect-[4/5]" />
);

const SplitCard = ({ branding, content }: CardProps) => (
    <div className="grid min-h-[140px] grid-cols-[1fr_42%]">
        <div className="flex flex-col justify-center gap-2 p-3">
            <BrandHeader branding={branding} hasLink={false} />
            <Title>{content.title}</Title>
            {content.text && (
                <p className="line-clamp-2 text-[13px] leading-[18px] text-white/75">
                    {content.text}
                </p>
            )}
        </div>
        <WidgetImage url={content.imageUrl} className="size-full" />
    </div>
);

const AccentCard = ({ branding, content }: CardProps) => (
    <div className="relative flex min-h-[140px] flex-col justify-between gap-4 p-4">
        <div className="absolute -right-6 -bottom-8 size-36 rotate-[-12deg] rounded-[2rem] bg-white/10" />
        <div className="flex items-center gap-2">
            <Logo branding={branding} />
            <span className="flex-1 truncate text-[11px] font-semibold tracking-widest text-white/85 uppercase">
                {branding.name}
            </span>
            {content.hasLink && (
                <ChevronRight className="size-3.5 text-white" />
            )}
        </div>
        <div className="relative flex max-w-[85%] flex-col gap-1">
            {content.title && (
                <p className="text-xl leading-6 font-semibold text-white">
                    {content.title}
                </p>
            )}
            {content.text && (
                <p className="text-[13px] leading-[18px] text-white/90">
                    {content.text}
                </p>
            )}
        </div>
    </div>
);

const StripCard = ({ branding, content }: CardProps) => (
    <div className="flex items-center gap-3 px-3 py-2.5">
        <Logo branding={branding} />
        <p className="flex-1 truncate text-sm font-semibold text-white">
            {content.title ?? content.text}
        </p>
        {content.hasLink && (
            <ChevronRight
                className="size-4 shrink-0"
                style={{ color: branding.accentColor }}
            />
        )}
    </div>
);

const CallToActionCard = ({ branding, content }: CardProps) => (
    <>
        <HeroCard branding={branding} content={content} />
        <div className="px-3 pb-3">
            <span
                className="inline-block rounded-full px-4 py-2 text-[13px] font-semibold text-white"
                style={{ background: branding.accentColor }}
            >
                {content.buttonLabel}
            </span>
        </div>
    </>
);

const TileCard = ({ branding, content }: CardProps) => (
    <div className="relative aspect-square">
        <WidgetImage url={content.imageUrl} className="size-full" />
        <div className="absolute inset-0 flex flex-col justify-between bg-gradient-to-t from-black/85 to-transparent p-2">
            <div className="h-5">
                <Logo branding={branding} />
            </div>
            <p className="line-clamp-2 text-[13px] leading-4 font-semibold text-white">
                {content.title}
            </p>
        </div>
    </div>
);

const CARDS: Record<
    CommunityWhitelabelDashboardWidgetLayout,
    (props: CardProps) => React.ReactElement
> = {
    [Layout.HERO]: HeroCard,
    [Layout.HEADLINE]: HeadlineCard,
    [Layout.COMPACT]: CompactCard,
    [Layout.BANNER]: BannerCard,
    [Layout.ACCENT]: AccentCard,
    [Layout.STRIP]: StripCard,
    [Layout.POSTER]: PosterCard,
    [Layout.SPLIT]: SplitCard,
    [Layout.CALL_TO_ACTION]: CallToActionCard,
    [Layout.TILE]: TileCard
};

function widgetContent(
    widget: CommunityWhitelabelDashboardWidgetDto
): WidgetContent {
    return {
        title: previewText(widget.title),
        text: previewText(widget.text),
        imageUrl: imageUrl(widget.image),
        buttonLabel: previewText(widget.buttonLabel),
        hasLink: Boolean(widget.link?.trim())
    };
}

function shownLayout(
    layout: CommunityWhitelabelDashboardWidgetLayout,
    content: WidgetContent,
    paired: boolean
): CommunityWhitelabelDashboardWidgetLayout {
    if (layout === Layout.TILE && !paired) {
        return shownLayout(Layout.BANNER, content, false);
    }
    const needsImage = [Layout.BANNER, Layout.POSTER, Layout.SPLIT];
    if (needsImage.includes(layout) && !content.imageUrl) {
        return Layout.HERO;
    }
    if (layout === Layout.CALL_TO_ACTION && !content.buttonLabel) {
        return Layout.HERO;
    }
    return layout;
}

const WidgetCard = ({
    branding,
    widget,
    paired
}: {
    branding: Branding;
    widget: CommunityWhitelabelDashboardWidgetDto;
    paired: boolean;
}) => {
    const content = widgetContent(widget);
    const layout = shownLayout(widget.layout, content, paired);
    const Card = CARDS[layout];
    const onAccent =
        layout === Layout.ACCENT ||
        (layout === Layout.TILE && !content.imageUrl);

    return (
        <div
            className="overflow-hidden rounded-2xl border border-white/10 bg-[#17181b]"
            style={onAccent ? { background: branding.accentColor } : undefined}
        >
            <Card branding={branding} content={content} />
        </div>
    );
};

export const DashboardWidgetPreview = ({
    branding,
    widgets
}: {
    branding: Branding;
    widgets: CommunityWhitelabelDashboardWidgetDto[];
}) => {
    const rows = rowsOfWidgets(
        widgets.filter((widget) => widget.enabled),
        (widget) => widget.layout === Layout.TILE
    );

    return (
        <div className="flex flex-col items-center gap-2">
            <div className="w-[320px] rounded-[2.25rem] border-[6px] border-neutral-800 bg-[#0b0b0d] px-3 pt-8 pb-6 shadow-lg">
                <p className="mb-3 text-lg font-semibold text-white">Home</p>
                <div className="flex max-h-[640px] min-h-[420px] flex-col gap-3 overflow-y-auto">
                    {rows.map((row, position) => (
                        <div
                            key={position}
                            className={
                                row.length === 2
                                    ? 'grid grid-cols-2 gap-2.5'
                                    : undefined
                            }
                        >
                            {row.map((widget, column) => (
                                <WidgetCard
                                    key={column}
                                    branding={branding}
                                    widget={widget}
                                    paired={row.length === 2}
                                />
                            ))}
                        </div>
                    ))}
                    {rows.length === 0 && (
                        <p className="py-16 text-center text-xs text-white/50">
                            No enabled widgets
                        </p>
                    )}
                </div>
            </div>
            <p className="text-xs text-muted-foreground">
                Enabled widgets in English, ignoring visibility dates.
            </p>
        </div>
    );
};
